import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
    Chip,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Switch,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import CustomCard from './CustomCard';
import { formatUtilityType, formatUtilityTypes } from '../utils/formatUtilityType';
import axiosClient from '../services/axiosClient';

export interface ExpectedBill {
    id: string;
    vendor_name: string;
    sender_domain: string | null;
    utility_types: string[];
    due_day: number;
    period_start_day: number;
    period_end_day: number;
    service_month_offset: number;
    active: boolean;
}

const UTILITY_TYPE_OPTIONS = [
    'electric',
    'gas',
    'water',
    'internet',
    'trash',
    'cleaning',
    'security',
    'other'
];

const DAY_MIN = 1;
const DAY_MAX = 28;
const OFFSET_MIN = 0;
const OFFSET_MAX = 3;

function dayBeforeDue(dueDay: number): number {
    return dueDay === 1 ? 28 : dueDay - 1;
}

function isValidDay(value: string): boolean {
    const n = Number(value);
    return value.trim() !== '' && Number.isInteger(n) && n >= DAY_MIN && n <= DAY_MAX;
}

function isValidOffset(value: string): boolean {
    const n = Number(value);
    return value.trim() !== '' && Number.isInteger(n) && n >= OFFSET_MIN && n <= OFFSET_MAX;
}

interface ExpectedBillsCardProps {
    householdId: string;
    expectedBills: ExpectedBill[];
    onExpectedBillsChange: (bills: ExpectedBill[]) => void;
    isCustomer?: boolean;
    currentRoommateIsAdmin?: boolean;
}

export default function ExpectedBillsCard({
    householdId,
    expectedBills,
    onExpectedBillsChange,
    isCustomer = false,
    currentRoommateIsAdmin = false
}: ExpectedBillsCardProps) {
    const canManage = isCustomer || currentRoommateIsAdmin;

    const [bills, setBills] = useState<ExpectedBill[]>(expectedBills);
    useEffect(() => {
        setBills(expectedBills);
    }, [expectedBills]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [vendorName, setVendorName] = useState('');
    const [senderDomain, setSenderDomain] = useState('');
    const [utilityTypes, setUtilityTypes] = useState<string[]>([]);
    const [dueDay, setDueDay] = useState('');
    const [periodStartDay, setPeriodStartDay] = useState('');
    const [periodEndDay, setPeriodEndDay] = useState('');
    const [serviceMonthOffset, setServiceMonthOffset] = useState('0');
    const [periodDaysTouched, setPeriodDaysTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const [deleteTarget, setDeleteTarget] = useState<ExpectedBill | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [toggleErrors, setToggleErrors] = useState<Record<string, string>>({});

    const updateBillsList = (updated: ExpectedBill) => {
        const next = bills.some((b) => b.id === updated.id)
            ? bills.map((b) => (b.id === updated.id ? updated : b))
            : [...bills, updated];
        setBills(next);
        onExpectedBillsChange(next);
    };

    const applyDueDayDefaults = (nextDueDay: string, resetTouched: boolean) => {
        const dueDayNumber = Number(nextDueDay);
        if (!isValidDay(nextDueDay)) return;
        if (resetTouched || !periodDaysTouched) {
            setPeriodStartDay(String(dueDayNumber));
            setPeriodEndDay(String(dayBeforeDue(dueDayNumber)));
        }
    };

    const handleDueDayChange = (value: string) => {
        setDueDay(value);
        applyDueDayDefaults(value, true);
        setPeriodDaysTouched(false);
    };

    const handleOpenAdd = () => {
        setEditingId(null);
        setVendorName('');
        setSenderDomain('');
        setUtilityTypes([]);
        setDueDay('');
        setPeriodStartDay('');
        setPeriodEndDay('');
        setServiceMonthOffset('0');
        setPeriodDaysTouched(false);
        setFormError('');
        setDialogOpen(true);
    };

    const handleOpenEdit = (bill: ExpectedBill) => {
        setEditingId(bill.id);
        setVendorName(bill.vendor_name);
        setSenderDomain(bill.sender_domain ?? '');
        setUtilityTypes(bill.utility_types ?? []);
        setDueDay(String(bill.due_day));
        setPeriodStartDay(String(bill.period_start_day));
        setPeriodEndDay(String(bill.period_end_day));
        setServiceMonthOffset(String(bill.service_month_offset ?? 0));
        setPeriodDaysTouched(true);
        setFormError('');
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        if (saving) return;
        setDialogOpen(false);
        setEditingId(null);
        setFormError('');
    };

    const handleToggleUtilityType = (type: string) => {
        setUtilityTypes((prev) => (
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        ));
    };

    const dueDayValid = isValidDay(dueDay);
    const periodStartValid = isValidDay(periodStartDay);
    const periodEndValid = isValidDay(periodEndDay);
    const offsetValid = isValidOffset(serviceMonthOffset);
    const formValid = vendorName.trim() && dueDayValid && periodStartValid && periodEndValid && offsetValid;

    const handleSubmit = async () => {
        if (!formValid) return;

        setSaving(true);
        setFormError('');
        const payload: Record<string, unknown> = {
            vendor_name: vendorName.trim(),
            sender_domain: senderDomain.trim() || null,
            utility_types: utilityTypes,
            due_day: Number(dueDay),
            service_month_offset: Number(serviceMonthOffset)
        };
        if (periodDaysTouched) {
            payload.period_start_day = Number(periodStartDay);
            payload.period_end_day = Number(periodEndDay);
        }

        try {
            const response = editingId
                ? await axiosClient.patch(`/util/dashboard/${householdId}/expected_bills/${editingId}`, payload)
                : await axiosClient.post(`/util/dashboard/${householdId}/expected_bills`, payload);

            updateBillsList(response.data.expected_bill);
            setDialogOpen(false);
            setEditingId(null);
        } catch (error: any) {
            setFormError(error?.response?.data?.error || 'Failed to save this roster line.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (bill: ExpectedBill) => {
        setTogglingId(bill.id);
        setToggleErrors((prev) => {
            const next = { ...prev };
            delete next[bill.id];
            return next;
        });
        try {
            const response = await axiosClient.patch(`/util/dashboard/${householdId}/expected_bills/${bill.id}`, {
                active: !bill.active
            });
            updateBillsList(response.data.expected_bill);
        } catch (error: any) {
            setToggleErrors((prev) => ({
                ...prev,
                [bill.id]: error?.response?.data?.error || 'Failed to update this roster line.'
            }));
        } finally {
            setTogglingId(null);
        }
    };

    const handleOpenDelete = (bill: ExpectedBill) => {
        setDeleteTarget(bill);
        setDeleteError('');
    };

    const handleCloseDelete = () => {
        if (deleting) return;
        setDeleteTarget(null);
        setDeleteError('');
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setDeleteError('');
        try {
            await axiosClient.delete(`/util/dashboard/${householdId}/expected_bills/${deleteTarget.id}`);
            const next = bills.filter((b) => b.id !== deleteTarget.id);
            setBills(next);
            onExpectedBillsChange(next);
            setDeleteTarget(null);
        } catch (error: any) {
            setDeleteError(error?.response?.data?.error || 'Failed to delete this roster line.');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <CustomCard>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography fontWeight="bold" variant="h6" sx={{ mb: 1 }}>
                        Expected bills
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The vendors you expect a bill from every month. We use this to know when a cycle's bills are
                        all in.
                    </Typography>
                    {bills.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            No expected bills yet.
                        </Typography>
                    ) : (
                        bills.map((bill) => (
                            <Box
                                key={bill.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    py: 1.5,
                                    borderBottom: '1px solid',
                                    borderColor: 'grey.200',
                                    opacity: bill.active ? 1 : 0.6
                                }}
                            >
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1" fontWeight={500}>
                                            {bill.vendor_name}
                                        </Typography>
                                        {!bill.active && (
                                            <Chip
                                                label="Inactive"
                                                size="small"
                                                variant="outlined"
                                                sx={{ color: '#666', borderColor: 'grey.400' }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatUtilityTypes(bill.utility_types)} &middot; Due day {bill.due_day}
                                        {' '}&middot; Period {bill.period_start_day}&ndash;{bill.period_end_day}
                                        {bill.service_month_offset > 0 && (
                                            <> &middot; Service offset {bill.service_month_offset}</>
                                        )}
                                    </Typography>
                                    {bill.sender_domain && (
                                        <Typography variant="body2" color="text.secondary">
                                            From {bill.sender_domain}
                                        </Typography>
                                    )}
                                    {toggleErrors[bill.id] && (
                                        <Typography variant="caption" color="error">
                                            {toggleErrors[bill.id]}
                                        </Typography>
                                    )}
                                </Box>
                                {canManage && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                        {togglingId === bill.id ? (
                                            <CircularProgress size={20} sx={{ mx: 1.5 }} />
                                        ) : (
                                            <Switch
                                                size="small"
                                                checked={bill.active}
                                                onChange={() => handleToggleActive(bill)}
                                                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'black' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'black' } }}
                                            />
                                        )}
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenEdit(bill)}
                                            sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: '8px', p: 1 }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenDelete(bill)}
                                            sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: '8px', p: 1 }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        ))
                    )}
                    {canManage && (
                        <PrimaryButton fullWidth onClick={handleOpenAdd} sx={{ mt: 2 }}>
                            Add expected bill
                        </PrimaryButton>
                    )}
                </CardContent>
            </CustomCard>

            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {editingId ? 'Edit expected bill' : 'Add expected bill'}
                    </Typography>
                    <IconButton onClick={handleCloseDialog} size="small" disabled={saving}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    {formError && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {formError}
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        label="Vendor name"
                        value={vendorName}
                        onChange={(e) => setVendorName(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Sender domain"
                        value={senderDomain}
                        onChange={(e) => setSenderDomain(e.target.value)}
                        placeholder="e.g. bge.com"
                        helperText="The address this vendor's bills come from — this is what we match inbound emails against, not the vendor name."
                        sx={{ mb: 2 }}
                    />
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                        Utility types
                    </Typography>
                    <FormGroup sx={{ mb: 2 }}>
                        {UTILITY_TYPE_OPTIONS.map((type) => (
                            <FormControlLabel
                                key={type}
                                control={
                                    <Checkbox
                                        checked={utilityTypes.includes(type)}
                                        onChange={() => handleToggleUtilityType(type)}
                                        sx={{ '&.Mui-checked': { color: 'black' } }}
                                    />
                                }
                                label={formatUtilityType(type)}
                            />
                        ))}
                    </FormGroup>
                    <TextField
                        fullWidth
                        type="number"
                        label="Due date (day of month, 1-28)"
                        value={dueDay}
                        onChange={(e) => handleDueDayChange(e.target.value)}
                        helperText="The day of the month this vendor's bill is due."
                        inputProps={{ min: DAY_MIN, max: DAY_MAX }}
                        error={dueDay.trim() !== '' && !dueDayValid}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        type="number"
                        label="Period start day (1-28)"
                        value={periodStartDay}
                        onChange={(e) => {
                            setPeriodStartDay(e.target.value);
                            setPeriodDaysTouched(true);
                        }}
                        helperText="Day of the month the billing period starts (defaults from due date)."
                        inputProps={{ min: DAY_MIN, max: DAY_MAX }}
                        error={periodStartDay.trim() !== '' && !periodStartValid}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        type="number"
                        label="Period end day (1-28)"
                        value={periodEndDay}
                        onChange={(e) => {
                            setPeriodEndDay(e.target.value);
                            setPeriodDaysTouched(true);
                        }}
                        helperText="Day of the month the billing period ends (defaults to the day before due date)."
                        inputProps={{ min: DAY_MIN, max: DAY_MAX }}
                        error={periodEndDay.trim() !== '' && !periodEndValid}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        type="number"
                        label="Service month offset (0-3)"
                        value={serviceMonthOffset}
                        onChange={(e) => setServiceMonthOffset(e.target.value)}
                        helperText="Months to shift the service period back from the due month (e.g. 1 if the bill covers last month's usage)."
                        inputProps={{ min: OFFSET_MIN, max: OFFSET_MAX }}
                        error={serviceMonthOffset.trim() !== '' && !offsetValid}
                        sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <SecondaryButton fullWidth onClick={handleCloseDialog} disabled={saving}>
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton
                            fullWidth
                            onClick={handleSubmit}
                            disabled={saving || !formValid}
                        >
                            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                        </PrimaryButton>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={!!deleteTarget}
                onClose={handleCloseDelete}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Delete {deleteTarget?.vendor_name}?
                    </Typography>
                    <IconButton onClick={handleCloseDelete} size="small" disabled={deleting}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This permanently removes the roster line. If it has ever matched a bill, deleting isn't
                        allowed — deactivate it instead.
                    </Typography>
                    {deleteError && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {deleteError}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <SecondaryButton fullWidth onClick={handleCloseDelete} disabled={deleting}>
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton fullWidth onClick={handleConfirmDelete} disabled={deleting}>
                            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                        </PrimaryButton>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
